import ExpanderList, { ExpanderListItem } from '@rio-cloud/rio-uikit/ExpanderList';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import {
    compoundManagementSlice,
    getActiveCompoundManagementView,
    getSelectedCompoundVehicle,
} from '../../../reducers/compoundManagementSlice';
import { AllowedAction, CompoundManagementView, InventoryVehicle } from '../../compoundManagement.types';
import { ArrivalDetails, ArrivalDetailsProps } from '../../shared/sidebar/ArrivalDetails';
import { CompoundLocationAndDestinationDetails } from '../../shared/sidebar/CompoundLocationAndDestinationDetails';
import { CompoundVehicleVIN } from '../../shared/sidebar/CompoundVehicleVIN';
import { VehicleDetails } from '../../shared/sidebar/VehicleDetails';
import { CancelledArrivalDetailsHeader } from '../../shared/sidebar/CancelledArrivalDetailsHeader';
import { ArrivalDetailsHeader } from '../../shared/sidebar/ArrivalDetailsHeader';
import { CustomsInformationExpander } from '../../../sharedComponents/sidebar/CustomsInformationExpander';
import { DepartureDetailsHeader } from '../../shared/sidebar/DepartureDetailsHeader';
import { UnannouncedDepartureDetails, UnannouncedDepartureDetailsProps } from './UnannouncedDepartureDetails';

export const VehicleInventorySidebar: FunctionComponent = () => {
    const dispatch = useAppDispatch();
    const selectedVehicle = useAppSelector(getSelectedCompoundVehicle) as InventoryVehicle;
    const activeManagementView = useAppSelector(getActiveCompoundManagementView);

    const sidebarProperties = {
        showHeaderBorder: true,
        resizable: true,
        position: Sidebar.RIGHT,
        minWidth: 400,
        maxWidth: 550,
        width: 550,
        fly: true,
    };

    const closeSidebar = () => {
        dispatch(compoundManagementSlice.actions.deselectCompoundVehicle());
    };

    if (selectedVehicle !== undefined && activeManagementView === CompoundManagementView.INVENTORY) {
        const title = <FormattedMessage id="outboundOrderBook.compoundManagement.common.sidebar.header.titleVehicle" />;

        const footerButton = (
            <div className="display-flex gap-10 flex-row-reverse">
                <button className="btn btn-default" type="button" onClick={closeSidebar}>
                    <FormattedMessage id="outboundOrderBook.common.button.close" />
                </button>
            </div>
        );

        const arrivalDetails = buildArrivalDetailsItem({
            compoundVehicleId: selectedVehicle.id,
            compoundLocation: selectedVehicle.compoundLocation,
            arrival: selectedVehicle.arrival,
            disabled: !selectedVehicle.allowedActions.includes(AllowedAction.REPORT_ARRIVAL),
        });

        const departureDetails = buildUnannouncedDepartureDetailsItem({
            compoundVehicleId: selectedVehicle.id,
            compoundLocation: selectedVehicle.compoundLocation,
            disabled: !selectedVehicle.allowedActions.includes(AllowedAction.REPORT_UNANNOUNCED_DEPARTURE),
        });

        const details = selectedVehicle.arrival.isCancelled ? [arrivalDetails] : [arrivalDetails, departureDetails];

        return (
            <Sidebar
                {...sidebarProperties}
                closed={false}
                onClose={closeSidebar}
                title={title}
                titleClassName="padding-left-10"
                footer={footerButton}
                footerClassName="margin-right-10"
            >
                <CompoundVehicleVIN vehicle={selectedVehicle.vehicle} />
                <CompoundLocationAndDestinationDetails
                    compoundLocation={selectedVehicle.compoundLocation}
                    vehicle={selectedVehicle.vehicle}
                />
                <ExpanderList items={details} className="margin-20" unmountOnExit={false} />
                <CustomsInformationExpander customsInformation={selectedVehicle.vehicle.customsInformation} />
                <VehicleDetails vehicle={selectedVehicle.vehicle} isCancelled={false} />
            </Sidebar>
        );
    } else {
        return <Sidebar {...sidebarProperties} closed />;
    }
};

const buildArrivalDetailsItem = (props: ArrivalDetailsProps): ExpanderListItem => {
    const isCancelled = props.arrival.isCancelled;

    return {
        id: 'arrival',
        open: true,
        header: isCancelled ? <CancelledArrivalDetailsHeader /> : <ArrivalDetailsHeader />,
        body: <ArrivalDetails {...props} />,
        bodyClassName: 'border-top-only border-width-1 border-style-solid border-color-light',
        className: isCancelled ? 'list-group-item-danger' : undefined,
    };
};

const buildUnannouncedDepartureDetailsItem = (props: UnannouncedDepartureDetailsProps): ExpanderListItem => {
    return {
        id: 'departure',
        open: false,
        header: <DepartureDetailsHeader />,
        body: <UnannouncedDepartureDetails {...props} />,
        bodyClassName: 'border-top-only border-width-1 border-style-solid border-color-light',
        className: undefined,
    };
};
